import React, { useLayoutEffect, useEffect } from 'react';
import { useParams } from 'react-router';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Providers } from './components/Providers';
import { RouteAuthGuard } from './components/RouteAuthGuard';
import CookieConsent from './components/CookieConsent';
import { UserType } from './types';
import Top from './pages/top';
import TermsPage from './pages/static/terms';
import PrivacyPolicy from './pages/static/privacy';
import CookiePolicy from './pages/static/cookie_policy';
import CommercePage from './pages/static/commerce';
import FaqPage from './pages/static/faq';
import FlowPage from './pages/static/flow';
import MethodPage from './pages/static/method';
import ToBeDonorPage from './pages/static/to_be_donor';
import ForumAboutPage from './pages/static/forum_about';
import SearchDonor from './pages/search_donor';
import SearchResult from './pages/search_result';
import Donor from './pages/donor';
import Login from './pages/login';
import Register from './pages/register';
import RegisterTemp from './pages/register_temp';
import Mypage from './pages/mypage';
import MypageTop from './pages/mypage_top';
import MypageMessage from './pages/mypage_message';
import MypageRecord from './pages/mypage_record';
import MypageRecordNew from './pages/mypage_record_new';
import MypageDonorProfile from './pages/mypage_donor_profile';
import MypageCheckup from './pages/mypage_checkup';
import MypageCheckupStd from './pages/mypage_checkup_std';
import MypageCheckupSemen from './pages/mypage_checkup_semen';
import MypageCheckupComplete from './pages/mypage_checkup_complete';
import MypageKyc from './pages/mypage_kyc';
import MypageReviews from './pages/mypage_reviews';
import MypageReviewsEdit from './pages/mypage_reviews/Edit';
import AccountPage from './pages/mypage_account';
import UserActivation from './pages/user_activation';
import EmailChangeConfirm from './pages/email_change/Confirm';
import PasswordForgot from './pages/forgot';
import PasswordReset from './pages/reset';
import Welcome from './pages/welcome';
import NotFoundPage from './pages/static/404';
import Unsubscribe from './pages/unsubscribe';
import ForumIndex from './pages/forum';
import ForumPostDetail from './pages/forum_post';
import MypageForum from './pages/mypage_forum';

const ScrollToTop = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [ location.pathname, location.search ]);
  return <>{ children }</>
}

const OauthRedirect = () => {
  const { provider, userType } = useParams();

  useEffect(() => {
    const query = userType ? `?user_type=${ userType }` : '';
    window.location.href = `${ process.env.REACT_APP_API_HOST }/oauth/${ provider }${ query }`;
  }, [provider, userType]);

  return <span>Redirecting...</span>;
}

function App() {
  return (
    <Providers>
      <BrowserRouter>
        <ScrollToTop>
          <ToastContainer hideProgressBar theme="colored"  />
          <CookieConsent />
          <Routes>
            <Route path="/" element={ <Top /> } />
            <Route path="terms" element={ <TermsPage /> } />
            <Route path="privacy" element={ <PrivacyPolicy /> } />
            <Route path="cookie_policy" element={ <CookiePolicy /> } />
            <Route path="commerce" element={ <CommercePage /> } />
            <Route path="faq" element={ <FaqPage /> } />
            <Route path="flow" element={ <FlowPage /> } />
            <Route path="method" element={ <MethodPage /> } />
            <Route path="to_be_donor" element={ <ToBeDonorPage /> } />
            <Route path="forum_about" element={ <ForumAboutPage /> } />
            <Route path="search" element={ <SearchDonor /> } />
            <Route path="search-result" element={ <SearchResult /> } />
            <Route path="donor/:user_no" element={ <Donor /> } />
            <Route path="forum" element={ <ForumIndex /> } />
            <Route path="forum/:id" element={ <ForumPostDetail /> } />
            <Route path="login" element={ <Login /> } />
            <Route path="register" element={ <Register /> } />
            <Route path="register_temp" element={ <RegisterTemp /> } />
            <Route path="password/forgot" element={ <PasswordForgot /> } />
            <Route path="password/reset/:token" element={ <PasswordReset /> } />
            <Route path="users/:token/activate" element={ <UserActivation /> } />
            <Route path="email/change/:token/confirm" element={ <EmailChangeConfirm /> } />
            <Route path="oauth/:provider" element={ <OauthRedirect /> } />
            <Route path="oauth/:provider/:userType" element={ <OauthRedirect /> } />
            <Route path="welcome" element={ <Welcome /> } />
            <Route path="unsubscribe/:subscType" element={ <Unsubscribe /> } />
            {/* Legacy email link redirects */}
            <Route path="messages" element={ <Navigate to="/mypage/messages" replace /> } />
            <Route path="mypage/std_semen" element={ <Navigate to="/mypage/checkups" replace /> } />
            <Route path="mypage/record" element={ <Navigate to="/mypage/records" replace /> } />
            <Route path="static/to_be_donor" element={ <Navigate to="/to_be_donor" replace /> } />
            <Route path="mypage" element={
              <RouteAuthGuard
                component={ <Mypage /> }
                redirect="/login"
                allowedUserTypes={[ UserType.Donor, UserType.Donee ]}
              />
            }>
              <Route path="" element={ <MypageTop /> } />
              <Route path="messages" element={ <MypageMessage /> } />
              <Route path="records" element={ <MypageRecord /> } />
              <Route path="records/new" element={ <MypageRecordNew /> } />
              <Route path="profile" element={ <MypageDonorProfile /> } />
              <Route path="account" element={ <AccountPage /> } />
              <Route path="checkups" element={ <MypageCheckup /> } />
              <Route path="checkups/std" element={ <MypageCheckupStd /> } />
              <Route path="checkups/semen" element={ <MypageCheckupSemen /> } />
              <Route path="checkups/complete" element={ <MypageCheckupComplete /> } />
              <Route path="kyc" element={ <MypageKyc /> } />
              <Route path="reviews/:donor_no" element={ <MypageReviewsEdit /> } />
              <Route path="reviews" element={ <MypageReviews /> } />
              <Route path="forum/*" element={ <MypageForum /> } />
            </Route>
            <Route path="*" element={ <NotFoundPage /> } />
          </Routes>
        </ScrollToTop>
      </BrowserRouter>
    </Providers>
  );
}

export default App;
